import { useCallback, useState } from "react";
import { toast } from "sonner";

import { SentimentType } from "@/types/document";

interface EvaluateSuggestionResponse {
  criteriaId?: string;
  sentiment?: SentimentType;
}

interface UseEvaluateNoteOptions {
  documentId?: string;
  evaluateSuggestion: (
    documentId: string,
    note: string,
    blockId?: string
  ) => Promise<EvaluateSuggestionResponse>;
  onResult?: (result: EvaluateSuggestionResponse) => void;
}

export const useEvaluateNote = ({
  documentId,
  evaluateSuggestion,
  onResult,
}: UseEvaluateNoteOptions) => {
  const [isEvaluating, setIsEvaluating] = useState(false);

  const runEvaluation = useCallback(
    async (content: string, shouldNotifyOnError: boolean, blockId?: string) => {
      if (!documentId || !content.trim()) return null;

      if (shouldNotifyOnError) {
        setIsEvaluating(true);
      }

      try {
        const response = await evaluateSuggestion(documentId, content, blockId);
        onResult?.(response);
        return response;
      } catch (error) {
        console.error("Failed to evaluate suggestion:", error);
        if (shouldNotifyOnError) {
          toast.error("Failed to evaluate the note. Please try again.");
        }
        return null;
      } finally {
        if (shouldNotifyOnError) {
          setIsEvaluating(false);
        }
      }
    },
    [documentId, evaluateSuggestion, onResult]
  );

  const evaluateNote = useCallback(
    async (content: string, blockId?: string) => runEvaluation(content, true, blockId),
    [runEvaluation]
  );

  const autoEvaluateNote = useCallback(
    async (content: string, blockId?: string) => runEvaluation(content, false, blockId),
    [runEvaluation]
  );

  return {
    evaluateNote,
    autoEvaluateNote,
    isEvaluating,
  } as const;
};
